import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCurrency, formatDateTime } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, MapPin, User, Car, IndianRupee, Clock, Route, CreditCard, Star } from "lucide-react";

const Field = ({ label, value, testid }) => (
    <div data-testid={testid}>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm text-navy-900 mt-1">{value || "—"}</div>
    </div>
);

const RideDetail = () => {
    const { id } = useParams();
    const [ride, setRide] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/rides/${id}`)
            .then((r) => setRide(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Failed to load"));
    }, [id]);

    if (error) return <div className="text-red-600">{error}</div>;
    if (!ride) return <div className="text-slate-500">Loading…</div>;

    return (
        <div className="space-y-6">
            <Link to="/rides" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-mint-600" data-testid="ride-back-link">
                <ArrowLeft className="w-4 h-4" /> Back to rides
            </Link>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Ride</div>
                    <h1 className="font-display font-bold text-2xl text-navy-900" data-testid="ride-id">{ride.id}</h1>
                </div>
                <div className="flex items-center gap-3">
                    <StatusBadge status={ride.status} />
                    <span className="text-xs text-slate-500">{formatDateTime(ride.created_at)}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    {/* Map placeholder */}
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        <div className="relative h-64 bg-gradient-to-br from-mint-50 via-white to-sky-50">
                            <img
                                src="https://images.pexels.com/photos/7321954/pexels-photo-7321954.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Map preview"
                                className="absolute inset-0 w-full h-full object-cover opacity-90"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-slate-200 rounded-xl p-3 text-xs flex items-center gap-2 shadow-sm">
                                <MapPin className="w-4 h-4 text-mint-600" />
                                <span className="font-semibold text-navy-900">Live map</span>
                                <span className="text-slate-500">— add your Google Maps API key in backend env to enable interactive tracking.</span>
                            </div>
                        </div>
                        <div className="p-5 grid sm:grid-cols-2 gap-4">
                            <Field label="Pickup" value={ride.pickup_address} testid="ride-pickup" />
                            <Field label="Drop" value={ride.drop_address} testid="ride-drop" />
                            <Field label="Distance" value={`${ride.distance_km} km`} />
                            <Field label="Duration" value={`${ride.duration_min} min`} />
                        </div>
                    </div>

                    {/* Fare breakdown */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="font-display font-semibold text-navy-900 mb-4 flex items-center gap-2">
                            <IndianRupee className="w-4 h-4 text-mint-600" /> Fare breakdown
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">Total fare</span><span className="font-semibold tabular-nums">{formatCurrency(ride.fare)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-600">Platform commission (18%)</span><span className="text-red-600 tabular-nums">- {formatCurrency(ride.commission)}</span></div>
                            <div className="flex justify-between pt-2 border-t border-slate-100"><span className="font-semibold text-navy-900">Driver payout</span><span className="font-semibold text-mint-600 tabular-nums">{formatCurrency(ride.driver_payout)}</span></div>
                            <div className="flex justify-between pt-2 text-slate-500"><span>Payment method</span><span className="capitalize flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> {ride.payment_method}</span></div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    {/* Customer */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="font-display font-semibold text-navy-900 mb-3 flex items-center gap-2">
                            <User className="w-4 h-4 text-mint-600" /> Customer
                        </div>
                        <div className="space-y-3 text-sm">
                            <Field label="Name" value={ride.customer?.name} />
                            <Field label="Phone" value={ride.customer?.phone} />
                            <Link to={`/customers/${ride.customer_id}`} className="text-mint-600 hover:underline text-sm" data-testid="ride-view-customer">
                                View profile →
                            </Link>
                        </div>
                    </div>

                    {/* Driver */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="font-display font-semibold text-navy-900 mb-3 flex items-center gap-2">
                            <Car className="w-4 h-4 text-mint-600" /> Driver
                        </div>
                        <div className="space-y-3 text-sm">
                            <Field label="Name" value={ride.driver?.name} />
                            <Field label="Phone" value={ride.driver?.phone} />
                            <Field label="Vehicle" value={`${ride.vehicle_type} · ${ride.driver?.vehicle_number}`} />
                            <Link to={`/drivers/${ride.driver_id}`} className="text-mint-600 hover:underline text-sm" data-testid="ride-view-driver">
                                View profile →
                            </Link>
                        </div>
                    </div>

                    {ride.rating_by_customer && (
                        <div className="bg-white border border-slate-200 rounded-2xl p-5">
                            <div className="font-display font-semibold text-navy-900 mb-3 flex items-center gap-2">
                                <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> Customer rating
                            </div>
                            <div className="text-3xl font-display font-bold text-navy-900">{ride.rating_by_customer}<span className="text-sm text-slate-400 font-normal"> / 5</span></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RideDetail;
